import { useState } from 'react';
import {
  useAdminUsers,
  useBanUser,
  useImpersonate,
  useAdminUserDetails,
  useAssignManualSubscription,
  useAdminPlans,
} from '../lib/queries.js';

export function UsersPage() {
  const [q, setQ] = useState('');
  const [searchQ, setSearchQ] = useState('');
  const { data, isLoading } = useAdminUsers(searchQ);
  const ban = useBanUser();
  const imp = useImpersonate();
  
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const { data: details, isLoading: isDetailsLoading } = useAdminUserDetails(selectedUserId);
  const { data: plansData } = useAdminPlans();
  const assignPlan = useAssignManualSubscription();

  const [editingSubWorkspaceId, setEditingSubWorkspaceId] = useState<string | null>(null);
  const [selectedPlanSlug, setSelectedPlanSlug] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [isUnlimited, setIsUnlimited] = useState<boolean>(true);

  const [impTarget, setImpTarget] = useState<{ id: string; email: string } | null>(null);
  const [impReason, setImpReason] = useState('');

  async function doImpersonate() {
    if (!impTarget || impReason.length < 5) return;
    await imp.mutateAsync({ id: impTarget.id, reason: impReason });
    setImpTarget(null);
    alert(`Impersonating ${impTarget.email}. Sessão de 30 minutos. Abra a app principal.`);
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Usuários</h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSearchQ(q);
        }}
        className="mb-4 flex gap-2"
      >
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="buscar por email ou nome"
          className="flex-1 rounded-md bg-zinc-900 border border-zinc-800 px-3 py-2 text-sm text-zinc-100"
        />
        <button type="submit" className="rounded-md bg-zinc-800 px-4 text-sm hover:bg-zinc-700 text-zinc-100">
          Buscar
        </button>
      </form>

      {isLoading && <div className="text-zinc-500">carregando…</div>}
      <div className="rounded-lg border border-zinc-800 overflow-hidden bg-zinc-900/20">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="text-left py-2 px-3">Email</th>
              <th className="text-left">Nome</th>
              <th className="text-left">Admin</th>
              <th className="text-left">Status</th>
              <th className="text-left">Criado em</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data?.users.map((u) => (
              <tr key={u.id} className="border-t border-zinc-800/50 hover:bg-zinc-900/25">
                <td className="py-2.5 px-3 font-mono text-xs text-zinc-300">{u.email}</td>
                <td className="text-zinc-300">{u.name ?? '—'}</td>
                <td>
                  {u.adminRole && (
                    <span className="text-rose-400 text-xs rounded bg-rose-950/40 px-1.5 py-0.5 font-mono">
                      {u.adminRole}
                    </span>
                  )}
                </td>
                <td>
                  {u.bannedAt ? (
                    <span className="text-rose-400 text-xs rounded bg-rose-950/20 px-1.5 py-0.5 border border-rose-950/30">banned</span>
                  ) : (
                    <span className="text-emerald-400 text-xs rounded bg-emerald-950/20 px-1.5 py-0.5 border border-emerald-950/30">ativo</span>
                  )}
                </td>
                <td className="text-xs text-zinc-500">{new Date(u.createdAt).toLocaleString()}</td>
                <td className="text-right px-3">
                  <button
                    onClick={() => setSelectedUserId(u.id)}
                    className="text-xs text-cyan-400 hover:text-cyan-300 px-2 py-1 rounded bg-cyan-950/20 border border-cyan-900/20 hover:border-cyan-850 transition-all font-semibold mr-2"
                  >
                    Gerenciar
                  </button>
                  <button
                    onClick={() => setImpTarget({ id: u.id, email: u.email })}
                    className="text-xs text-zinc-400 hover:text-zinc-100 px-2 py-1 font-semibold"
                  >
                    impersonate
                  </button>
                </td>
              </tr>
            ))}
            {data?.users.length === 0 && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-zinc-500">
                  nenhum usuário
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedUserId && (
        <div className="fixed inset-0 bg-black/65 grid place-items-center p-6 z-50 overflow-y-auto" onClick={() => {
          setSelectedUserId(null);
          setEditingSubWorkspaceId(null);
        }}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-2xl w-full max-h-[90dvh] overflow-y-auto"
          >
            {isDetailsLoading ? (
              <div className="text-zinc-500 py-6 text-center font-semibold">carregando detalhes…</div>
            ) : details ? (
              <div>
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-zinc-100">{details.user.name || 'Sem nome'}</h2>
                    <p className="text-sm text-zinc-500 font-mono">{details.user.email}</p>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedUserId(null);
                      setEditingSubWorkspaceId(null);
                    }}
                    className="text-zinc-400 hover:text-zinc-100 text-xs bg-zinc-800 hover:bg-zinc-700 px-3 py-1.5 rounded transition-colors font-semibold"
                  >
                    Fechar
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-zinc-950/60 p-3 rounded-lg border border-zinc-800/60">
                    <span className="text-[10px] uppercase text-zinc-500 block mb-1 font-bold">Status da Conta</span>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${details.user.bannedAt ? 'bg-rose-500' : 'bg-emerald-500'}`}></span>
                      <span className="text-sm font-semibold">{details.user.bannedAt ? 'Banido' : 'Ativo'}</span>
                    </div>
                  </div>
                  <div className="bg-zinc-950/60 p-3 rounded-lg border border-zinc-800/60">
                    <span className="text-[10px] uppercase text-zinc-500 block mb-1 font-bold">Data de Cadastro</span>
                    <span className="text-sm font-semibold">{new Date(details.user.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Ações Rápidas */}
                <div className="mb-6 border-t border-zinc-800/60 pt-4">
                  <h3 className="text-[11px] uppercase text-zinc-500 font-bold mb-3">Ações Administrativas</h3>
                  <div className="flex gap-3">
                    <button
                      onClick={() => ban.mutate({ id: details.user.id, banned: !details.user.bannedAt, reason: 'admin action' })}
                      className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                        details.user.bannedAt
                          ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-800/40 hover:bg-emerald-900/60'
                          : 'bg-rose-950/60 text-rose-400 border border-rose-800/40 hover:bg-rose-900/60'
                      }`}
                    >
                      {details.user.bannedAt ? 'Desbanir Usuário' : 'Banir Usuário'}
                    </button>
                    <button
                      onClick={() => setImpTarget({ id: details.user.id, email: details.user.email })}
                      className="bg-cyan-950/60 text-cyan-400 border border-cyan-800/40 px-3 py-1.5 rounded text-xs font-semibold hover:bg-cyan-900/60 transition-all"
                    >
                      Impersonate (Log in as)
                    </button>
                  </div>
                </div>

                {/* Workspaces */}
                <div className="border-t border-zinc-800/60 pt-4">
                  <h3 className="text-[11px] uppercase text-zinc-500 font-bold mb-3">Workspaces & Planos</h3>
                  
                  {details.workspaces.length === 0 ? (
                    <p className="text-sm text-zinc-500 py-2">Nenhum workspace associado.</p>
                  ) : (
                    <div className="space-y-4">
                      {details.workspaces.map((w) => {
                        const sub = details.subscriptions.find((s) => s.workspaceId === w.workspaceId);
                        const isEditing = editingSubWorkspaceId === w.workspaceId;

                        return (
                          <div key={w.workspaceId} className="bg-zinc-950/30 border border-zinc-800/60 rounded-lg p-4 hover:border-zinc-850 transition-all">
                            <div className="flex justify-between items-center mb-2">
                              <div>
                                <h4 className="font-semibold text-sm text-zinc-100">{w.name || 'Workspace sem nome'}</h4>
                                <p className="text-xs text-zinc-500 font-mono">{w.workspaceId}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] bg-zinc-800/80 text-zinc-400 px-2 py-0.5 rounded font-mono">
                                  {w.role}
                                </span>
                                <span className={`text-[10px] px-2 py-0.5 rounded uppercase font-bold font-mono ${
                                  sub?.planSlug === 'pro'
                                    ? 'bg-cyan-950/80 text-cyan-400 border border-cyan-900/30'
                                    : sub?.planSlug === 'team'
                                    ? 'bg-indigo-950/80 text-indigo-400 border border-indigo-900/30'
                                    : 'bg-zinc-800/80 text-zinc-400'
                                }`}>
                                  {sub?.planName || 'Free/Default'}
                                </span>
                              </div>
                            </div>

                            <div className="text-xs text-zinc-500 mb-3">
                              {sub ? (
                                <span>
                                  Status: <strong className="text-zinc-400 uppercase text-[10px]">{sub.status}</strong>
                                  {sub.currentPeriodEnd ? ` | Expira em: ${new Date(sub.currentPeriodEnd).toLocaleDateString()}` : ' | Vitalício / Ilimitado'}
                                </span>
                              ) : (
                                <span>Sem assinatura ativa (Plano Padrão)</span>
                              )}
                            </div>

                            {isEditing ? (
                              <div className="bg-zinc-900/80 border border-zinc-800/80 rounded-lg p-4 mt-3 space-y-4">
                                <h5 className="text-[10px] font-bold text-zinc-400 uppercase tracking-wider">Atribuir Plano Manualmente</h5>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div>
                                    <label className="text-[10px] uppercase text-zinc-500 block mb-1 font-bold">Selecionar Plano</label>
                                    <select
                                      value={selectedPlanSlug}
                                      onChange={(e) => setSelectedPlanSlug(e.target.value)}
                                      className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-2 text-xs text-zinc-300 focus:outline-none focus:border-cyan-500 font-medium"
                                    >
                                      <option value="">-- Escolha um plano --</option>
                                      {plansData?.plans.map((p) => (
                                        <option key={p.id} value={p.slug}>
                                          {p.name} (R$ {(p.priceBrl/100).toFixed(2)})
                                        </option>
                                      ))}
                                    </select>
                                  </div>

                                  <div>
                                    <label className="text-[10px] uppercase text-zinc-500 block mb-1 font-bold">Expiração</label>
                                    <div className="flex flex-col gap-2">
                                      <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                                        <input
                                          type="checkbox"
                                          checked={isUnlimited}
                                          onChange={(e) => {
                                            setIsUnlimited(e.target.checked);
                                            if (e.target.checked) setExpiresAt('');
                                          }}
                                          className="rounded border-zinc-800 bg-zinc-950 text-cyan-500 focus:ring-0 focus:ring-offset-0"
                                        />
                                        Vitalício / Sem limite
                                      </label>
                                      {!isUnlimited && (
                                        <input
                                          type="date"
                                          value={expiresAt}
                                          onChange={(e) => setExpiresAt(e.target.value)}
                                          className="w-full bg-zinc-950 border border-zinc-850 rounded px-2.5 py-1.5 text-xs text-zinc-200 focus:outline-none focus:border-cyan-500"
                                        />
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="flex justify-end gap-2 pt-2 border-t border-zinc-800/40">
                                  <button
                                    onClick={() => setEditingSubWorkspaceId(null)}
                                    className="px-3 py-1.5 text-xs text-zinc-400 hover:text-zinc-100 font-semibold"
                                  >
                                    Cancelar
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!selectedPlanSlug) return alert('Selecione um plano!');
                                      await assignPlan.mutateAsync({
                                        userId: details.user.id,
                                        workspaceId: w.workspaceId,
                                        planSlug: selectedPlanSlug,
                                        expiresAt: isUnlimited ? null : expiresAt ? new Date(expiresAt).toISOString() : null,
                                      });
                                      setEditingSubWorkspaceId(null);
                                    }}
                                    disabled={assignPlan.isPending}
                                    className="px-3.5 py-1.5 bg-cyan-500 text-zinc-950 font-bold text-xs rounded hover:bg-cyan-400 disabled:opacity-50 transition-colors shadow-md shadow-cyan-950/20"
                                  >
                                    {assignPlan.isPending ? 'Salvando…' : 'Salvar Alteração'}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-end mt-2">
                                <button
                                  onClick={() => {
                                    setEditingSubWorkspaceId(w.workspaceId);
                                    setSelectedPlanSlug(sub?.planSlug ?? '');
                                    setExpiresAt(sub && sub.currentPeriodEnd ? (new Date(sub.currentPeriodEnd).toISOString().split('T')[0] ?? '') : '');
                                    setIsUnlimited(!sub || !sub.currentPeriodEnd);
                                  }}
                                  className="text-xs bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white px-3 py-1.5 rounded hover:border-cyan-500 transition-all font-semibold"
                                >
                                  Alterar Plano
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-zinc-500 py-6 text-center">Nenhum detalhe encontrado.</div>
            )}
          </div>
        </div>
      )}

      {impTarget && (
        <div className="fixed inset-0 bg-black/60 grid place-items-center p-6 z-50">
          <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-semibold mb-2 text-zinc-100">Impersonate</h2>
            <p className="text-sm text-zinc-400 mb-3">
              Alvo: <code>{impTarget.email}</code>. Sessão de 30 minutos. Razão obrigatória ≥5
              caracteres. Tudo gravado no audit log.
            </p>
            <textarea
              value={impReason}
              onChange={(e) => setImpReason(e.target.value)}
              placeholder="motivo (ticket, debug, etc.)"
              className="w-full rounded bg-zinc-950 border border-zinc-800 p-2 text-sm h-24 text-zinc-200 focus:outline-none focus:border-cyan-500"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setImpTarget(null)}
                className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-100 font-semibold"
              >
                cancelar
              </button>
              <button
                onClick={doImpersonate}
                disabled={impReason.length < 5 || imp.isPending}
                className="px-3 py-1.5 text-sm bg-rose-500 text-zinc-950 rounded disabled:opacity-50 font-bold hover:bg-rose-400 transition-colors"
              >
                {imp.isPending ? 'gerando…' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
